import { logEvent, setUserId, setUserProperties } from 'firebase/analytics';
import { trace, PerformanceTrace } from 'firebase/performance';

import { analytics, perf } from '../firebase';

type EventParams = Record<string, string | number | boolean>;

export function logAnalyticsEvent(name: string, params?: EventParams): void {
  if (!analytics) return;
  logEvent(analytics, name, params);
}

export function setAnalyticsUserId(uid: string | null): void {
  if (!analytics) return;
  setUserId(analytics, uid ?? '');
}

export function setAnalyticsVersion(version: string): void {
  if (!analytics) return;
  setUserProperties(analytics, { app_version: version });
}

export function setAnalyticsEnvironment(): void {
  if (!analytics) return;
  type UABrand = { brand: string; version: string };
  type UAData = { brands: UABrand[]; platform: string };
  const uad = (navigator as Navigator & { userAgentData?: UAData }).userAgentData;
  const screen_size = `${screen.width}x${screen.height}`;

  if (uad) {
    const brand = uad.brands.find((b: UABrand) => !b.brand.includes('Not') && b.brand !== 'Chromium');
    setUserProperties(analytics, {
      browser: brand?.brand ?? 'Chromium',
      browser_version: brand?.version ?? '',
      os: uad.platform,
      screen_size,
    });
  } else {
    // Safari / Firefox fallback — parse UA string
    const ua = navigator.userAgent;
    const safari = /Version\/([\d.]+).*Safari/.exec(ua);
    const firefox = /Firefox\/([\d.]+)/.exec(ua);
    const os = /Macintosh/.test(ua) ? 'macOS'
      : /Windows/.test(ua) ? 'Windows'
      : /iPhone|iPad/.test(ua) ? 'iOS'
      : /Android/.test(ua) ? 'Android'
      : 'Unknown';
    setUserProperties(analytics, {
      browser: safari ? 'Safari' : firefox ? 'Firefox' : 'Unknown',
      browser_version: (safari ?? firefox)?.[1] ?? '',
      os,
      screen_size,
    });
  }
}

export function startPerfTrace(name: string): PerformanceTrace | null {
  if (!perf) return null;
  const t = trace(perf, name);
  t.start();
  return t;
}

export function logError(context: string, error: unknown): void {
  const err = error as { message?: string; code?: string };
  console.error(`[${context}]`, error);
  logAnalyticsEvent('app_error', {
    context,
    message: (err.message ?? String(error)).slice(0, 100),
    ...(err.code ? { code: err.code } : {}),
  });
}
